import React, { useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Sync as SyncIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { useAuth } from '../../context/AuthContext';

export default function EmailAccounts() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.ruolo === 'admin';
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    imap_host: '',
    imap_port: 993,
    imap_secure: true,
    imap_user: '',
    imap_password: '',
    smtp_host: '',
    smtp_port: 587,
    smtp_secure: false,
    smtp_user: '',
    smtp_password: '',
    active: true,
    sync_enabled: true,
    sync_frequency_minutes: 2
  });
  const [error, setError] = useState('');

  // Query accounts
  const { data: accounts = [], isLoading } = useQuery({
    queryKey: ['email-accounts'],
    queryFn: () => api.get('/email-accounts').then(res => res.data)
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data) => api.post('/email-accounts', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-accounts'] });
      handleCloseDialog();
    },
    onError: (err) => {
      setError(err.response?.data?.error || err.response?.data?.message || err.message);
    }
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => api.put(`/email-accounts/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-accounts'] });
      handleCloseDialog();
    },
    onError: (err) => {
      setError(err.response?.data?.error || err.response?.data?.message || err.message);
    }
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/email-accounts/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-accounts'] });
    }
  });

  // Test mutation
  const testMutation = useMutation({
    mutationFn: (id) => api.post(`/email-accounts/${id}/test`)
  });

  // Sync mutation
  const syncMutation = useMutation({
    mutationFn: (id) => api.post(`/email-accounts/${id}/sync`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-accounts'] });
    }
  });

  const handleOpenDialog = (account = null) => {
    if (account) {
      setEditingAccount(account);
      setFormData({
        name: account.name,
        email: account.email,
        imap_host: account.imap_host,
        imap_port: account.imap_port,
        imap_secure: account.imap_secure,
        imap_user: account.imap_user,
        imap_password: '',
        smtp_host: account.smtp_host,
        smtp_port: account.smtp_port,
        smtp_secure: account.smtp_secure,
        smtp_user: account.smtp_user,
        smtp_password: '',
        active: account.active,
        sync_enabled: account.sync_enabled,
        sync_frequency_minutes: account.sync_frequency_minutes
      });
    } else {
      setEditingAccount(null);
      setFormData({
        name: '',
        email: '',
        imap_host: '',
        imap_port: 993,
        imap_secure: true,
        imap_user: '',
        imap_password: '',
        smtp_host: '',
        smtp_port: 587,
        smtp_secure: false,
        smtp_user: '',
        smtp_password: '',
        active: true,
        sync_enabled: true,
        sync_frequency_minutes: 2
      });
    }
    setError('');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingAccount(null);
    setError('');
  };

  const handleSave = () => {
    if (!isAdmin) {
      setError('Accesso riservato agli amministratori');
      return;
    }
    if (editingAccount) {
      updateMutation.mutate({ id: editingAccount.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = (id) => {
    if (!isAdmin) {
      alert('Accesso riservato agli amministratori');
      return;
    }
    if (window.confirm('Eliminare questo account email? Tutte le email associate saranno eliminate.')) {
      deleteMutation.mutate(id);
    }
  };

  const handleTest = async (id) => {
    if (!isAdmin) {
      alert('Accesso riservato agli amministratori');
      return;
    }
    try {
      await testMutation.mutateAsync(id);
      alert('Connessione IMAP riuscita!');
    } catch (err) {
      alert(`Errore connessione: ${err.response?.data?.error || err.response?.data?.message || err.message}`);
    }
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 3 } }}>
      <Paper>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Account Email
          </Typography>
          {isAdmin ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Nuovo Account
            </Button>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Solo gli amministratori possono creare/modificare account
            </Typography>
          )}
        </Toolbar>

        {isLoading ? (
          <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress /></Box>
        ) : accounts.length === 0 ? (
          <Typography sx={{ p: 3, textAlign: 'center' }} color="text.secondary">Nessun account configurato</Typography>
        ) : isMobile ? (
          <Box sx={{ p: 1 }}>
            {accounts.map((account) => (
              <Paper key={account.id} variant="outlined" sx={{ p: 2, mb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography fontWeight="bold">{account.name}</Typography>
                  {account.active && account.sync_enabled ? (
                    <Chip label="Attivo" color="success" size="small" />
                  ) : account.active ? (
                    <Chip label="No sync" color="warning" size="small" />
                  ) : (
                    <Chip label="Inattivo" size="small" />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, wordBreak: 'break-all' }}>{account.email}</Typography>
                {account.last_sync_at && (
                  <Typography variant="caption" color="text.secondary">
                    Sync: {format(new Date(account.last_sync_at), 'dd/MM HH:mm', { locale: it })}
                    {account.last_sync_error && ' ⚠️'}
                  </Typography>
                )}
                {isAdmin && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, gap: 0.5 }}>
                    <IconButton size="small" onClick={() => handleTest(account.id)} disabled={testMutation.isLoading}><CheckIcon /></IconButton>
                    <IconButton size="small" onClick={() => syncMutation.mutate(account.id)} disabled={syncMutation.isLoading}><SyncIcon /></IconButton>
                    <IconButton size="small" onClick={() => handleOpenDialog(account)}><EditIcon /></IconButton>
                    <IconButton size="small" onClick={() => handleDelete(account.id)}><DeleteIcon /></IconButton>
                  </Box>
                )}
              </Paper>
            ))}
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>IMAP</TableCell>
                  <TableCell>SMTP</TableCell>
                  <TableCell>Stato</TableCell>
                  <TableCell>Ultimo Sync</TableCell>
                  <TableCell>Azioni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {accounts.map((account) => (
                  <TableRow key={account.id}>
                    <TableCell>{account.name}</TableCell>
                    <TableCell>{account.email}</TableCell>
                    <TableCell>
                      {account.imap_host}:{account.imap_port}
                      {account.imap_secure && <> 🔒</>}
                    </TableCell>
                    <TableCell>
                      {account.smtp_host}:{account.smtp_port}
                      {account.smtp_secure && <> 🔒</>}
                    </TableCell>
                    <TableCell>
                      {account.active && account.sync_enabled ? (
                        <Chip label="Attivo" color="success" size="small" />
                      ) : account.active ? (
                        <Chip label="Attivo (no sync)" color="warning" size="small" />
                      ) : (
                        <Chip label="Inattivo" size="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      {account.last_sync_at ? (
                        <span title={account.last_sync_error}>
                          {format(new Date(account.last_sync_at), 'dd/MM HH:mm', { locale: it })}
                          {account.last_sync_error && <> ⚠️</>}
                        </span>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {isAdmin ? (
                        <>
                          <IconButton 
                            size="small" 
                            onClick={() => handleTest(account.id)}
                            disabled={testMutation.isLoading}
                            title="Test connessione"
                          >
                            <CheckIcon />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            onClick={() => syncMutation.mutate(account.id)}
                            disabled={syncMutation.isLoading}
                            title="Sincronizza ora"
                          >
                            <SyncIcon />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleOpenDialog(account)}>
                            <EditIcon />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleDelete(account.id)}>
                            <DeleteIcon />
                          </IconButton>
                        </>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          {editingAccount ? 'Modifica Account Email' : 'Nuovo Account Email'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Nome Account"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            sx={{ mt: 2, mb: 2 }}
          />

          <TextField
            fullWidth
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            sx={{ mb: 2 }}
          />

          <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>Configurazione IMAP</Typography>

          <TextField
            fullWidth
            label="IMAP Host"
            value={formData.imap_host}
            onChange={(e) => setFormData({ ...formData, imap_host: e.target.value })}
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              label="Porta IMAP"
              type="number"
              value={formData.imap_port}
              onChange={(e) => setFormData({ ...formData, imap_port: parseInt(e.target.value) })}
              sx={{ width: 150 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.imap_secure}
                  onChange={(e) => setFormData({ ...formData, imap_secure: e.target.checked })}
                />
              }
              label="SSL/TLS"
            />
          </Box>

          <TextField
            fullWidth
            label="IMAP Username"
            value={formData.imap_user}
            onChange={(e) => setFormData({ ...formData, imap_user: e.target.value })}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label={editingAccount ? "IMAP Password (lascia vuoto per non modificare)" : "IMAP Password"}
            type="password"
            value={formData.imap_password}
            onChange={(e) => setFormData({ ...formData, imap_password: e.target.value })}
            sx={{ mb: 2 }}
          />

          <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>Configurazione SMTP</Typography>

          <TextField
            fullWidth
            label="SMTP Host"
            value={formData.smtp_host}
            onChange={(e) => setFormData({ ...formData, smtp_host: e.target.value })}
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              label="Porta SMTP"
              type="number"
              value={formData.smtp_port}
              onChange={(e) => setFormData({ ...formData, smtp_port: parseInt(e.target.value) })}
              sx={{ width: 150 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.smtp_secure}
                  onChange={(e) => setFormData({ ...formData, smtp_secure: e.target.checked })}
                />
              }
              label="SSL/TLS"
            />
          </Box>

          <TextField
            fullWidth
            label="SMTP Username (opzionale, default = IMAP user)"
            value={formData.smtp_user}
            onChange={(e) => setFormData({ ...formData, smtp_user: e.target.value })}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="SMTP Password (opzionale, default = IMAP password)"
            type="password"
            value={formData.smtp_password}
            onChange={(e) => setFormData({ ...formData, smtp_password: e.target.value })}
            sx={{ mb: 2 }}
          />

          <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>Opzioni</Typography>

          <FormControlLabel
            control={
              <Switch
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
              />
            }
            label="Account attivo"
            sx={{ display: 'block', mb: 1 }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={formData.sync_enabled}
                onChange={(e) => setFormData({ ...formData, sync_enabled: e.target.checked })}
              />
            }
            label="Sincronizzazione automatica"
            sx={{ display: 'block', mb: 2 }}
          />

          <TextField
            label="Frequenza sync (minuti)"
            type="number"
            value={formData.sync_frequency_minutes}
            onChange={(e) => setFormData({ ...formData, sync_frequency_minutes: parseInt(e.target.value) })}
            sx={{ width: 200 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annulla</Button>
          <Button 
            variant="contained" 
            onClick={handleSave}
            disabled={createMutation.isLoading || updateMutation.isLoading}
          >
            {(createMutation.isLoading || updateMutation.isLoading) ? <CircularProgress size={24} /> : 'Salva'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
